import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import axios from "axios";
import { Check, ArrowRight, Loader2 } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SubscriptionSuccess() {
  const [searchParams] = useSearchParams();
  const { refreshUser } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState("checking");
  const [attempts, setAttempts] = useState(0);
  const sessionId = searchParams.get("session_id");
  const fromAdiInvite = searchParams.get("from") === "adi-invite";

  useEffect(() => {
    if (sessionId) {
      checkStatus();
    } else {
      setStatus("error");
    }
  }, [sessionId]);

  const checkStatus = async () => {
    try {
      const { data } = await axios.get(
        `${API_URL}/api/subscription/status/${sessionId}`,
        { withCredentials: true }
      );
      if (data.payment_status === "paid") {
        setStatus("success");
        await refreshUser();
      } else if (data.status === "expired") {
        setStatus("error");
      } else if (attempts < 8) {
        setTimeout(() => {
          setAttempts((prev) => prev + 1);
          checkStatus();
        }, 2000);
      } else {
        setStatus("error");
      }
    } catch {
      if (attempts < 8) {
        setTimeout(() => {
          setAttempts((prev) => prev + 1);
          checkStatus();
        }, 2000);
      } else {
        setStatus("error");
      }
    }
  };

  return (
    <div className="min-h-dvh no-overflow-x bg-white flex items-center justify-center px-6">
      <div className="w-full max-w-sm">
        {status === "checking" && (
          <div data-testid="subscription-checking" className="text-center">
            <Loader2 className="w-12 h-12 text-slate-900 animate-spin mx-auto mb-6" />
            <h1 className="text-xl font-bold text-slate-900 mb-2">Getting your account ready</h1>
            <p className="text-sm text-slate-500">Just a moment.</p>
          </div>
        )}

        {status === "success" && (
          <div data-testid="subscription-success" className="text-center">
            <div className="w-16 h-16 bg-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-8 h-8 text-white" strokeWidth={3} />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">You're in. Welcome aboard.</h1>
            <p className="text-sm text-slate-500 mb-8">
              {fromAdiInvite
                ? "Payment confirmed. Your ADI link is active and your guided PDI setup is ready."
                : "Everything's unlocked. Let's get you test-ready."}
            </p>

            <div className="bg-slate-50 rounded-xl p-4 mb-8 text-left space-y-3">
              {[
                "Every lesson — ready when you are",
                "Your progress, tracked properly",
                "Guidance that shapes around you",
                "Your instructor's eyes on your driving",
              ].map((f, i) => (
                <div key={i} className="flex items-center gap-2.5">
                  <Check className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                  <span className="text-sm text-slate-700">{f}</span>
                </div>
              ))}
            </div>

            <Button
              data-testid="go-to-dashboard-btn"
              onClick={() => navigate(fromAdiInvite ? "/onboarding/wizard" : "/dashboard", { replace: true })}
              className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white font-bold rounded-xl"
            >
              Take me to my dashboard
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        )}

        {status === "error" && (
          <div data-testid="subscription-error" className="text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <span className="text-2xl text-red-500 font-bold">!</span>
            </div>
            <h1 className="text-xl font-bold text-slate-900 mb-2">That didn't go through</h1>
            <p className="text-sm text-slate-500 mb-8">We couldn't confirm the payment. If you were charged, drop us a line — we'll sort it.</p>
            <Button
              data-testid="back-to-dashboard-btn"
              onClick={() => navigate("/dashboard")}
              variant="outline"
              className="w-full h-12 rounded-xl"
            >
              Back to Dashboard
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
